﻿import React, { Suspense, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';

import Login from '@features/login';
import { RouterBridge } from '@router-compat';

import { FN_HOME, FN_PREFIX, isFnCompany } from '../constants/fnCompanies';
import { hasPermission, ROLES } from '../constants/roles';
import { DRIVER_MODES, resolveDriverMode } from '../hooks/useDriverMode';
import { resolveCallerOrderEditPolicy } from '../hooks/useOrderEditPolicy';
import { isSuperManager } from '../modules/logic/managerScope';
import Footer from '../shared/layout/footer';
import Navbar from '../shared/layout/navbar';
import Sidebar from '../shared/layout/sidebar';
import { setCompany, setUserRole, setUser, resetStoreOnLogout } from '../store/actions';
import { fetchCurrentManager } from '../store/actions/ManagersActions';

import NotFound from './NotFound';
import RouteErrorBoundary from './RouteErrorBoundary';
// Not lazy: the catch-all must render even when a chunk fails to resolve.

import './App.css';

// =====================================================================
// Lazy-loaded routes
// =====================================================================

const UserPage = React.lazy(() => import('@features/clients/users/UserDetail'));
const CompanyUsers = React.lazy(() => import('@features/clients/companies/CompanyUsers'));

const MapView = React.lazy(() => import('@features/map'));
const History = React.lazy(() => import('@features/history'));
const HistoryFn = React.lazy(() => import('@features/history_fn'));
const AggiungiViaggio = React.lazy(() => import('@features/home/aggiungi'));
const Rubrica = React.lazy(() => import('@features/clients/users'));
const DettaglioRubrica = React.lazy(() => import('@features/clients/users/aggiungi'));

const Gestori = React.lazy(() => import('@features/clients/managers'));
const ManagerDetail = React.lazy(() => import('@features/clients/managers/ManagerDetail'));
const Contratti = React.lazy(() => import('@features/clients/contracts'));
const ContractDetail = React.lazy(() => import('@features/clients/contracts/ContractDetail'));
const Calendar = React.lazy(() => import('@features/home/Calendar/calendar'));
const AnalyticsHome = React.lazy(() => import('@features/analytics/home'));
const AnalyticsB2CMarket = React.lazy(() => import('@features/analytics/b2c-market'));
const AnalyticsOperators = React.lazy(() => import('@features/analytics/operators'));
const AnalyticsForecast = React.lazy(() => import('@features/analytics/forecast'));
const AnalyticsCashForecast = React.lazy(() => import('@features/analytics/cash_forecast'));
const DailyGroup = React.lazy(() => import('@features/planning/daily_trips/daily_group'));
const UsersRole = React.lazy(() => import('@features/settings/role_users'));
const Companies = React.lazy(() => import('@features/clients/companies'));
const GroupedTripsPage = React.lazy(() => import('@features/home/GrupedTrips/groupedTripsPage'));
const Invoices = React.lazy(() => import('@features/invoices'));
const Taxes = React.lazy(() => import('@features/taxes'));
const GeofencePage = React.lazy(() => import('@features/settings/geofences/GeofencePage'));
const WebsiteItemDetail = React.lazy(() => import('@features/analytics/website-item-detail'));
const KpiSegmentDetail = React.lazy(() => import('@features/analytics/kpi-segment-detail'));

const Dashboard = React.lazy(() => import('@features/home/control'));
const Timeline = React.lazy(() => import('@features/home/Timeline'));
const TimelineFn = React.lazy(() => import('@features/home/TimelineFn'));
const ServicesMap = React.lazy(() => import('@features/home/ServicesMap'));
const TripGroup = React.lazy(() => import('@features/home/TripGroup'));
const NewTrip = React.lazy(() => import('@features/home/Trip'));
const NewTripFn = React.lazy(() => import('@features/home/TripFn'));
const Event = React.lazy(() => import('@features/home/Event'));
const BookingList = React.lazy(() => import('@features/home/Booking'));
const BookingDetail = React.lazy(() => import('@features/home/Booking/BookingDetail'));
const BookingWizard = React.lazy(() => import('@features/home/Booking/BookingWizard'));

const CompanyDetail = React.lazy(() => import('@features/clients/companies/CompanyDetail'));

const AnalyticsDetails = React.lazy(() => import('@features/analytics/home/details'));
const DriversAnalytics = React.lazy(() => import('@features/analytics/drivers'));
const VehiclesAnalytics = React.lazy(() => import('@features/analytics/vehicles'));
const KPI = React.lazy(() => import('@features/analytics/kpi'));

const FleetVehicles = React.lazy(() => import('@features/fleet/vehicles'));
const FleetVehiclesAdd = React.lazy(() => import('@features/fleet/vehicles/VehicleForm'));
const FleetVehicleDetail = React.lazy(() => import('@features/fleet/vehicles/VehicleDetail'));

const FleetDrivers = React.lazy(() => import('@features/fleet/drivers'));
const FleetDriversAdd = React.lazy(() => import('@features/fleet/drivers/DriverForm'));
const FleetDriverDetail = React.lazy(() => import('@features/fleet/drivers/DriverDetail'));
const FleetDriverLive = React.lazy(() => import('@features/fleet/drivers/live'));
const FleetDriversSchedule = React.lazy(
  () => import('@features/fleet/drivers/components/FleetDriversSchedule')
);
const FleetDriverWarnings = React.lazy(() => import('@features/fleet/drivers/DriverWarningsList'));
const FleetDriverAbsences = React.lazy(() => import('@features/fleet/drivers/DriverAbsences'));

const FleetHub = React.lazy(() => import('@features/fleet/hub'));
const FleetHubAdd = React.lazy(() => import('@features/fleet/hub/HubForm'));
const FleetHubDetail = React.lazy(() => import('@features/fleet/hub/HubDetail'));

const FleetEquipment = React.lazy(() => import('@features/fleet/equipments'));
const FleetEquipmentAdd = React.lazy(() => import('@features/fleet/equipments/EquipmentForm'));

// Fuori dalla sidebar ma pagine a sé: si raggiungono dai pulsanti in barra
// della pagina Mezzi.
const FleetMaintenance = React.lazy(() => import('@features/fleet/maintenance'));
const FleetDamages = React.lazy(() => import('@features/fleet/damages'));
const FleetRefuelings = React.lazy(() => import('@features/fleet/refuelings'));
const FleetViolations = React.lazy(() => import('@features/fleet/violations'));
const FleetProcesses = React.lazy(() => import('@features/fleet/processes'));
const FleetProcessDetail = React.lazy(() => import('@features/fleet/processes/ProcessDetail'));
const FleetProcessTypeEditor = React.lazy(
  () => import('@features/fleet/processes/ProcessTypeEditor')
);
const FleetMaintenanceForm = React.lazy(
  () => import('@features/fleet/maintenance/MaintenanceForm')
);
const FleetMaintenanceDeadlines = React.lazy(
  () => import('@features/fleet/maintenance/MaintenanceDeadlines')
);

const Users = React.lazy(() => import('@features/clients/users'));

const DailyTrips = React.lazy(() => import('@features/planning/daily_trips'));
const DailyTripPage = React.lazy(() => import('@features/planning/daily_trips/daily_trip'));
const CdiPlanning = React.lazy(() => import('@features/planning/car_pooling'));
const CdiUsersPage = React.lazy(
  () => import('@features/planning/car_pooling/views/users/CarPoolingUsers')
);
const CdiRoutesPage = React.lazy(
  () => import('@features/planning/car_pooling/views/routes/CarPoolingRoutes')
);
const CdiAbsencePage = React.lazy(
  () => import('@features/planning/car_pooling/views/absence/CarPoolingAbsence')
);
const CdiKmReportPage = React.lazy(
  () => import('@features/planning/car_pooling/views/km_report/CarPoolingKmReport')
);
const Planner = React.lazy(() => import('@features/planning/planner'));

// =====================================================================
// Route guards (functional, v7-native)
// =====================================================================

/** Qualsiasi utente autenticato. */
function AuthGate({ children }) {
  const user = useSelector((state) => state.user);
  if (!user) return <Navigate to="/" replace />;
  return children;
}

/** Admin / super / administrative (o permission custom). */
function AdminGate({ children, permission = 'VIEW_ALL_DATA' }) {
  const user = useSelector((state) => state.user);
  if (!user) return <Navigate to="/" replace />;
  if (!hasPermission(user.role, permission)) return <Navigate to="/home" replace />;
  return children;
}

/** Anagrafica gestori: admin MBILITY oppure super gestore (vede/gestisce i propri associati). */
function ManageManagersGate({ children }) {
  const user = useSelector((state) => state.user);
  const currentManager = useSelector((state) => state.currentManager);
  if (!user) return <Navigate to="/" replace />;
  if (!hasPermission(user.role, 'VIEW_ALL_DATA') && !isSuperManager(currentManager)) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

/** Modulo abilitato sul gestore corrente (admin/super/administrative bypass). */
const MODULE_ADMIN_ROLES = [ROLES.ADMIN, ROLES.SUPER, ROLES.ADMINISTRATIVE];
function ModuleGate({ children, module: moduleName }) {
  const user = useSelector((state) => state.user);
  const currentManager = useSelector((state) => state.currentManager);
  const role = useSelector((state) => state.role);

  const hasModule =
    MODULE_ADMIN_ROLES.includes(role) || currentManager?.modules?.[moduleName] === true;

  if (!user) return <Navigate to="/" replace />;
  if (!hasModule) return <Navigate to="/home" replace />;
  return children;
}

/**
 * Pagine che presuppongono un'anagrafica autisti: non hanno senso per un gestore
 * in modalità matricola (vedi hooks/useDriverMode), dove gli operatori sono
 * numeri di matricola sugli ordini. Gli admin MBILITY mantengono l'accesso, come
 * per ModuleGate.
 */
function DriverModeGate({ children }) {
  const user = useSelector((state) => state.user);
  const currentManager = useSelector((state) => state.currentManager);
  const role = useSelector((state) => state.role);

  if (!user) return <Navigate to="/" replace />;
  if (
    !MODULE_ADMIN_ROLES.includes(role) &&
    resolveDriverMode(currentManager) === DRIVER_MODES.MATRICOLA
  ) {
    return <Navigate to="/home" replace />;
  }
  return children;
}

/**
 * The legacy order form (`/edit-trip/:id`) knows nothing about the order edit
 * policy: a limited manager lands on the order detail, which applies it.
 */
function LegacyOrderFormGate({ children }) {
  const user = useSelector((state) => state.user);
  const currentManager = useSelector((state) => state.currentManager);
  const location = useLocation();
  const { id } = useParams();

  if (resolveCallerOrderEditPolicy(user, currentManager).restricted) {
    return <Navigate to={`/home/trips/${id}`} state={location.state} replace />;
  }
  return children;
}

/**
 * Pages with a company-specific copy: a user of an FN company (see
 * constants/fnCompanies) lands on the same path under `/fn`, query, hash and
 * navigation state included — or on `to`, for a page replaced by another one
 * (the dashboard by the timeline).
 */
function FnRedirectGate({ children, to }) {
  const user = useSelector((state) => state.user);
  const location = useLocation();

  if (!user) return <Navigate to="/" replace />;
  if (isFnCompany(user.company)) {
    const { pathname, search, hash, state } = location;
    const target = to ?? `${FN_PREFIX}${pathname}${search}${hash}`;
    return <Navigate to={target} state={state} replace />;
  }
  return children;
}

// Helper per ridurre il rumore JSX nelle Routes
const auth = (el) => <AuthGate>{el}</AuthGate>;
const admin = (el, permission) => <AdminGate permission={permission}>{el}</AdminGate>;
const mod = (el, moduleName) => <ModuleGate module={moduleName}>{el}</ModuleGate>;
const manageManagers = (el) => <ManageManagersGate>{el}</ManageManagersGate>;
// Richiede l'anagrafica autisti (vedi DriverModeGate).
const records = (el) => <DriverModeGate>{el}</DriverModeGate>;
// Ha una copia sotto `/fn` per le company FN (vedi FnRedirectGate).
const fn = (el, to) => <FnRedirectGate to={to}>{el}</FnRedirectGate>;

// =====================================================================
// App
// =====================================================================

export default function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [userLoaded, setUserLoaded] = useState(false);

  // Listener Firebase agganciato UNA volta sola al mount. `lastUidRef`
  // tiene traccia dell'ultimo UID visto: navighiamo a `/home` solo alla
  // transizione di sign-in (no-user → user nuovo), non a ogni invocazione
  // del callback (che Firebase può rifare su token refresh, ecc.).
  const lastUidRef = useRef(null);

  useEffect(() => {
    const unsubscribe = window.firebase.auth().onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser) {
        // Logout (or expired/absent session): stop listeners, wipe the Redux
        // store and the persisted user so nothing leaks into the next session.
        dispatch(resetStoreOnLogout());
        localStorage.removeItem('user');
        lastUidRef.current = null;
        setUserLoaded(true);
        navigate('/');
        return;
      }

      const previousUid = lastUidRef.current;
      const isFirstSignIn = previousUid !== firebaseUser.uid;
      lastUidRef.current = firebaseUser.uid;

      try {
        const doc = await window.firebase
          .firestore()
          .collection('users')
          .doc(firebaseUser.uid)
          .get();
        const userData = { ...doc.data(), id: doc.id };

        let role = 'user';
        if (userData.role) {
          const rolePriority = [
            'admin',
            'super',
            'administrative',
            'gestore',
            'b2b',
            'cdi',
            'visualizzatore',
            'autista',
          ];
          const matched = rolePriority.find((r) => userData.role[r] === true);
          if (matched) role = matched;
        }

        dispatch(setCompany(userData.company));
        dispatch(setUserRole(role));
        dispatch(setUser(userData));
        localStorage.setItem('user', JSON.stringify(userData));

        const gestoreId = userData.gestore || userData.id_gestore;
        if (gestoreId) {
          dispatch(fetchCurrentManager(gestoreId));
        }

        if (isFirstSignIn) {
          navigate('/home');
        }
      } catch (err) {
        console.error('Error loading user data:', err);
      } finally {
        setUserLoaded(true);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
    // Listener da agganciare una sola volta. `dispatch`/`navigate` sono
    // stabili in react-redux/react-router v7 ma li teniamo fuori dalle
    // deps per evitare ri-registrazioni accidentali su HMR.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!userLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div className="wrapper">
      <RouterBridge />
      <Suspense fallback={<div>Loading...</div>}>
        <Navbar />
        <Sidebar />

        <RouteErrorBoundary key={location.pathname} navigate={navigate}>
          <Routes>
            <Route path="/" element={<Login />} />

            {/* Authenticated */}
            <Route path="/map" element={auth(<MapView />)} />
            <Route path="/history" element={fn(<History />)} />
            <Route path="/fn/history" element={auth(<HistoryFn />)} />
            <Route
              path="/edit-trip/:id"
              element={auth(
                <LegacyOrderFormGate>
                  <AggiungiViaggio />
                </LegacyOrderFormGate>
              )}
            />
            <Route path="/edit-group-trip/:id" element={auth(<TripGroup />)} />
            <Route path="/daily" element={auth(<DailyTrips />)} />
            <Route path="/daily-details/:id" element={auth(<DailyTripPage />)} />
            <Route path="/contracts" element={auth(<Contratti />)} />
            <Route path="/contracts/:id" element={auth(<ContractDetail />)} />
            <Route path="/rubrica/:id" element={auth(<DettaglioRubrica />)} />
            <Route path="/rubrica" element={auth(<Rubrica />)} />
            <Route path="/services-map" element={auth(<ServicesMap />)} />
            <Route path="/home/trips/group/:id" element={auth(<TripGroup />)} />
            <Route path="/home" element={fn(<Dashboard />, FN_HOME)} />
            <Route path="/home/trips/:id" element={fn(<NewTrip />)} />
            <Route path="/fn/home/trips/:id" element={auth(<NewTripFn />)} />
            <Route path="/home/timeline" element={fn(<Timeline />)} />
            <Route path="/fn/home/timeline" element={auth(<TimelineFn />)} />
            <Route path="/home/calendario" element={auth(<Calendar />)} />
            <Route path="/home/grouped-trips" element={auth(<GroupedTripsPage />)} />
            <Route path="/home/bookings" element={mod(<BookingList />, 'bookings')} />
            <Route path="/home/bookings/new" element={mod(<BookingWizard />, 'bookings')} />
            <Route path="/home/bookings/:id" element={mod(<BookingDetail />, 'bookings')} />
            <Route path="/home/events/:id" element={auth(<Event />)} />
            <Route path="/fleet/vehicles" element={mod(<FleetVehicles />, 'fleet')} />
            {/* Tab della pagina Mezzi: segmenti statici, quindi hanno la
              precedenza su `/fleet/vehicles/:id` nel ranking di React Router. */}
            <Route path="/fleet/vehicles/types" element={mod(<FleetVehicles />, 'fleet')} />
            <Route
              path="/fleet/vehicles/new-vehicle"
              element={mod(<FleetVehiclesAdd />, 'fleet')}
            />
            <Route path="/fleet/vehicles/:id" element={mod(<FleetVehicleDetail />, 'fleet')} />
            <Route
              path="/fleet/vehicles/:id/dotazioni"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/permits"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/documents"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/checks"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/inspections"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/damages"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/refuelings"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/maintenance"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/assignments"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/violations"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route
              path="/fleet/vehicles/:id/costs"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route path="/fleet/vehicles/:id/gps" element={mod(<FleetVehicleDetail />, 'fleet')} />
            <Route
              path="/fleet/vehicles/:id/events"
              element={mod(<FleetVehicleDetail />, 'fleet')}
            />
            <Route path="/fleet/drivers" element={mod(records(<FleetDrivers />), 'fleet')} />
            <Route
              path="/fleet/drivers/new-driver"
              element={mod(records(<FleetDriversAdd />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/all/schedule"
              element={mod(records(<FleetDriversSchedule />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/all/warnings"
              element={mod(records(<FleetDriverWarnings />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/all/absences"
              element={mod(records(<FleetDriverAbsences />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/equipments"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/courses"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/contracts"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/compliance"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/account"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/documents"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/checks"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/performance"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/warnings"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/assignments"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/events"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/shifts"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/violations"
              element={mod(records(<FleetDriverDetail />), 'fleet')}
            />
            <Route
              path="/fleet/drivers/:id/live"
              element={mod(records(admin(<FleetDriverLive />, 'VIEW_ALL_DATA')), 'fleet')}
            />
            <Route path="/fleet/hubs" element={mod(<FleetHub />, 'fleet')} />
            <Route path="/fleet/hubs/new-hub" element={mod(<FleetHubAdd />, 'fleet')} />
            <Route path="/fleet/hubs/:id" element={mod(<FleetHubDetail />, 'fleet')} />
            <Route path="/fleet/hubs/:id/documents" element={mod(<FleetHubDetail />, 'fleet')} />
            <Route path="/fleet/equipments" element={mod(<FleetEquipment />, 'fleet')} />
            <Route path="/fleet/equipments/:id" element={mod(<FleetEquipmentAdd />, 'fleet')} />
            <Route path="/fleet/damages" element={mod(<FleetDamages />, 'fleet')} />
            <Route path="/fleet/refuelings" element={mod(<FleetRefuelings />, 'fleet')} />
            <Route path="/fleet/violations" element={mod(<FleetViolations />, 'fleet')} />
            <Route path="/fleet/maintenance" element={mod(<FleetMaintenance />, 'fleet')} />
            <Route path="/fleet/processes" element={mod(<FleetProcesses />, 'fleet')} />
            {/* Segmenti statici prima di `:id`, come per i mezzi. */}
            <Route
              path="/fleet/processes/types/new"
              element={mod(<FleetProcessTypeEditor />, 'fleet')}
            />
            <Route
              path="/fleet/processes/types/:id"
              element={mod(<FleetProcessTypeEditor />, 'fleet')}
            />
            <Route path="/fleet/processes/:id" element={mod(<FleetProcessDetail />, 'fleet')} />
            <Route path="/fleet/maintenance" element={mod(<FleetMaintenance />, 'fleet')} />
            <Route path="/fleet/maintenance/new" element={mod(<FleetMaintenanceForm />, 'fleet')} />
            <Route
              path="/fleet/maintenance/scadenzario"
              element={mod(<FleetMaintenanceDeadlines />, 'fleet')}
            />
            <Route path="/fleet/maintenance/:id" element={mod(<FleetMaintenanceForm />, 'fleet')} />
            <Route path="/daily-trips/:id" element={auth(<DailyTripPage />)} />
            <Route path="/daily-trips/group/:id" element={auth(<DailyGroup />)} />

            {/* Module-gated */}
            <Route path="/users" element={mod(<Users />, 'users')} />
            <Route path="/users/:id" element={mod(<UserPage />, 'users')} />
            <Route path="/planning/daily-trips" element={mod(<DailyTrips />, 'daily_trips')} />
            <Route
              path="/planning/daily-trips/:id"
              element={mod(<DailyTripPage />, 'daily_trips')}
            />
            <Route path="/planning/car-pooling" element={mod(<CdiPlanning />, 'cdi')} />
            <Route path="/planning/car-pooling/:id" element={mod(<CdiRoutesPage />, 'cdi')} />
            <Route path="/planning/car-pooling/:id/users" element={mod(<CdiUsersPage />, 'cdi')} />
            <Route
              path="/planning/car-pooling/:id/absence"
              element={mod(<CdiAbsencePage />, 'cdi')}
            />
            <Route
              path="/planning/car-pooling/:id/report-km"
              element={mod(<CdiKmReportPage />, 'cdi')}
            />
            <Route path="/planning/planner" element={mod(<Planner />, 'planner')} />

            {/* Admin-only */}
            <Route path="/gestori" element={manageManagers(<Gestori />)} />
            <Route path="/gestori/:id" element={manageManagers(<ManagerDetail />)} />
            <Route path="/companies" element={mod(<Companies />, 'companies')} />
            <Route path="/companies/:id" element={mod(<CompanyDetail />, 'companies')} />
            <Route
              path="/companies/:companyId/users/*"
              element={mod(<CompanyUsers />, 'companies')}
            />
            <Route path="/analytics/home" element={admin(<AnalyticsHome />)} />
            <Route path="/analytics/b2c-market" element={admin(<AnalyticsB2CMarket />)} />
            <Route path="/analytics/b2c-market/:type/:id" element={admin(<WebsiteItemDetail />)} />
            <Route path="/analytics/operators" element={admin(<AnalyticsOperators />)} />
            <Route path="/analytics/home/details" element={admin(<AnalyticsDetails />)} />
            <Route path="/analytics/vehicles" element={admin(<VehiclesAnalytics />)} />
            <Route path="/analytics/drivers" element={admin(<DriversAnalytics />)} />
            <Route path="/analytics/forecast" element={admin(<AnalyticsForecast />)} />
            <Route path="/analytics/cash-forecast" element={admin(<AnalyticsCashForecast />)} />
            <Route path="/analytics/kpi" element={admin(<KPI />)} />
            <Route path="/analytics/kpi/cdi-detail" element={admin(<KpiSegmentDetail />)} />
            <Route path="/analytics/kpi/b2b-detail" element={admin(<KpiSegmentDetail />)} />
            <Route path="/analytics/kpi/b2c-detail" element={admin(<KpiSegmentDetail />)} />
            <Route path="/invoices" element={admin(<Invoices />)} />
            <Route path="/taxes" element={admin(<Taxes />)} />

            <Route path="/fleet/geofences" element={admin(<GeofencePage />)} />

            {/* Admin / super only */}
            <Route path="/users-role" element={admin(<UsersRole />, 'MANAGE_USERS')} />

            {/* Catch-all: an unregistered path must not render as a blank page. */}
            <Route path="*" element={auth(<NotFound />)} />
          </Routes>
        </RouteErrorBoundary>

        <Footer />
      </Suspense>
    </div>
  );
}
